import { useRef, useState } from "react";
import type { CosmeticId } from "../progress/schema";
import type { ProgressSnapshot } from "../progress/session";
import { Header, type NavigationProps } from "./SiteHeader";
import { CosmeticPreview } from "./CosmeticPreview";
import { cosmeticCopy, cosmeticIds, unlockProgress } from "./cosmetics";
import { Icon } from "./Icon";

export function AppearancePage({
  progress,
  onBack,
  onEquip,
  onNavigate,
}: NavigationProps & {
  readonly progress: ProgressSnapshot;
  readonly onBack: () => void;
  readonly onEquip: (cosmetic: CosmeticId) => void;
}) {
  const {
    ownedCosmetics: owned,
    equippedCosmetic: equipped,
    records,
    settings,
  } = progress.progress;
  const [selected, setSelected] = useState<CosmeticId>(equipped);
  const detailRef = useRef<HTMLElement>(null);
  const preview = (id: CosmeticId) => {
    setSelected(id);
    if (window.matchMedia("(max-width: 800px)").matches) {
      detailRef.current?.scrollIntoView({
        block: "start",
        behavior:
          settings.reducedMotion ||
          window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "instant"
            : "smooth",
      });
    }
  };
  const item = cosmeticCopy[selected];
  const unlock = unlockProgress(selected, records);
  const isOwned = owned[selected] === true;
  const count = cosmeticIds.filter((id) => owned[id] === true).length;
  const equip = (id: CosmeticId) => {
    setSelected(id);
    onEquip(id);
  };
  return (
    <>
      <Header onNavigate={onNavigate} progress={progress} active="appearance" />
      <main className="panel-page appearance-page">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">02 / THE COLLECTION</p>
            <h1>每道光，都有个性。</h1>
            <p className="panel-intro">六种原创外观，找到属于你的那一道。</p>
          </div>
          <button className="ghost-button" onClick={onBack}>
            <Icon name="back" />
            返回
          </button>
        </div>
        <div className="collection-layout">
          <section
            ref={detailRef}
            className="collection-detail"
            aria-label="皮肤预览"
          >
            <div className="detail-art">
              <span className="section-number">{item.subtitle}</span>
              <div className="showcase-orbit" aria-hidden="true" />
              <CosmeticPreview
                cosmetic={selected}
                theme={settings.theme}
                variant="detail"
              />
              <span className="material-label">动态纹理 · 原创外观</span>
            </div>
            <div className="detail-copy">
              <div className="detail-title">
                <h2>{item.name}</h2>
                <span className="quiet-badge">
                  <Icon name={isOwned ? "check" : "lock"} />
                  {selected === equipped
                    ? "当前外观"
                    : isOwned
                      ? "已拥有"
                      : "未解锁"}
                </span>
              </div>
              <p>{item.description}</p>
              {!isOwned ? (
                <div className="unlock-progress">
                  <div>
                    <span>{item.condition}</span>
                    <strong>
                      {Math.min(unlock.value, unlock.target)} / {unlock.target}{" "}
                      {unlock.unit}
                    </strong>
                  </div>
                  <progress
                    value={Math.min(unlock.value, unlock.target)}
                    max={unlock.target}
                    aria-label={item.condition}
                  />
                </div>
              ) : (
                <p className="unlocked-note">
                  <Icon name="check" />
                  已收藏，随时可以换上它出发。
                </p>
              )}
              <button
                className="primary-button"
                disabled={!isOwned || selected === equipped}
                onClick={() => equip(selected)}
              >
                <Icon name={isOwned ? "check" : "lock"} />
                {selected === equipped
                  ? "已装备"
                  : isOwned
                    ? "装备这款外观"
                    : "尚未解锁"}
              </button>
            </div>
          </section>
          <section className="collection-catalog" aria-label="全部外观">
            <div className="catalog-heading">
              <h2>你的收藏</h2>
              <span>{count} / 6 已拥有</span>
            </div>
            <div className="cosmetic-grid">
              {cosmeticIds.map((id) => {
                const skin = cosmeticCopy[id];
                const available = owned[id] === true;
                const current = equipped === id;
                return (
                  <article
                    className="cosmetic-card"
                    key={id}
                    data-locked={!available}
                    data-selected={selected === id}
                  >
                    <button
                      className="preview-button"
                      aria-label={`预览 ${skin.name}`}
                      aria-pressed={selected === id}
                      onClick={() => preview(id)}
                    >
                      <CosmeticPreview cosmetic={id} theme={settings.theme} />
                      <span className="preview-check" aria-hidden="true">
                        {selected === id ? (
                          <Icon name="check" />
                        ) : (
                          <Icon name={available ? "spark" : "lock"} />
                        )}
                      </span>
                    </button>
                    <div className="cosmetic-card-copy">
                      <span className="catalog-status">
                        {available ? "Owned" : "Locked"}
                      </span>
                      <h3>{skin.name}</h3>
                      <p>{skin.condition}</p>
                      <button
                        className="card-equip"
                        disabled={!available || current}
                        onClick={() => equip(id)}
                      >
                        {current ? "已装备" : available ? "装备" : "尚未解锁"}
                        {current ? (
                          <Icon name="check" />
                        ) : available ? (
                          <Icon name="arrow" />
                        ) : (
                          <Icon name="lock" />
                        )}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
            <p className="collection-fairness">
              <Icon name="shield" />
              外观不改变速度、碰撞或得分。解锁来自正式竞技对局。
            </p>
          </section>
        </div>
      </main>
    </>
  );
}
